import type { Voice } from '@listening/api-contract/legacy-client-types';
import IconPause from '@listening/assets/astro/icons/icon-pause.svg?react';
import IconPlay from '@listening/assets/astro/icons/icon-play.svg?react';

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as reactUseAudioPlayer from 'react-use-audio-player';

const VoiceSelectionBlock: React.FC<{ voices: Array<Voice> }> = ({
  voices,
}) => {
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const progressIntervalRef = useRef<number | null>(null);
  const [progressValues, setProgressValues] = useState<Record<string, number>>(
    {},
  );
  const [containerStates, setContainerStates] = useState<
    Record<string, boolean>
  >({});
  const audioPlayer = reactUseAudioPlayer.useAudioPlayer();
  const { getPosition, duration, isPlaying, pause, load } = audioPlayer;

  // Memoize the progress update function
  const updateAudioProgress = useCallback(() => {
    if (!currentlyPlaying) return;

    const position = getPosition();

    if (duration && typeof position === 'number') {
      const progress = (position / duration) * 100;
      setProgressValues((prev) => ({
        ...prev,
        [currentlyPlaying]: progress,
      }));
    }
  }, [currentlyPlaying, getPosition, duration]);

  // Track audio progress
  useEffect(() => {
    if (!(currentlyPlaying && isPlaying)) {
      return;
    }

    // Update immediately and then every 100ms
    updateAudioProgress();
    const intervalId = setInterval(updateAudioProgress, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, [currentlyPlaying, isPlaying, updateAudioProgress]);

  const stopProgressSimulation = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const stopAllAudio = useCallback(() => {
    if (isPlaying) {
      pause();
    }
    stopProgressSimulation();
    setCurrentlyPlaying(null);
    setContainerStates({});
    setProgressValues({});
  }, [isPlaying, pause, stopProgressSimulation]);

  // Listen for external audio events
  useEffect(() => {
    const handleStopAllAudio = () => {
      if (currentlyPlaying) {
        stopAllAudio();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('stopAllWavesurfers', handleStopAllAudio);
      window.addEventListener('audioStarted', handleStopAllAudio);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('stopAllWavesurfers', handleStopAllAudio);
        window.removeEventListener('audioStarted', handleStopAllAudio);
      }
    };
  }, [currentlyPlaying, stopAllAudio]);

  const updateProgress = (voiceName: string, progress: number) => {
    setProgressValues((prev) => ({ ...prev, [voiceName]: progress }));
  };

  const startProgressSimulation = (voiceName: string) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    let progress = 0;
    progressIntervalRef.current = window.setInterval(() => {
      progress = (progress + 1) % 101;
      updateProgress(voiceName, progress);

      // Auto-stop when reaching 100%
      if (progress >= 100) {
        setTimeout(() => {
          if (currentlyPlaying === voiceName) {
            handlePlayPause(voiceName);
          }
        }, 500);
      }
    }, 100);
  };

  const playAudio = (voiceName: string) => {
    // First, ensure all audio is stopped
    stopAllAudio();

    // Stop any playing audio in other components
    if (typeof window !== 'undefined') {
      const stopEvent = new CustomEvent('stopAllWavesurfers');
      window.dispatchEvent(stopEvent);

      // Notify other components that we're starting audio
      const audioStartedEvent = new CustomEvent('audioStarted');
      window.dispatchEvent(audioStartedEvent);
    }

    const voice = voices.find((v) => v.name === voiceName);
    if (voice?.sample_url) {
      try {
        load(voice.sample_url, {
          format: 'mp3',
          autoplay: true,
          html5: true,
          onload: () => {
            setCurrentlyPlaying(voiceName);
            setContainerStates((prev) => ({ ...prev, [voiceName]: true }));
          },
          onend: () => {
            setCurrentlyPlaying(null);
            setContainerStates((prev) => ({ ...prev, [voiceName]: false }));
            setProgressValues((prev) => ({ ...prev, [voiceName]: 0 }));
          },
        });
      } catch {
        // Fallback to simulation if audio loading fails
        startProgressSimulation(voiceName);
      }
    } else {
      // Fallback to simulation if no audio URL
      startProgressSimulation(voiceName);
    }
  };

  const handlePlayPause = (voiceName: string) => {
    if (currentlyPlaying === voiceName) {
      // Pause current voice
      stopAllAudio();
    } else {
      // Play new voice
      playAudio(voiceName);
    }
  };

  return (
    <div className='grow'>
      <div
        aria-label='Available voices'
        className='grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 lg:grid-cols-2 xl:grid-cols-4'
        role='list'
      >
        {voices.map((voice) => (
          <div
            className='relative flex flex-col items-center rounded-lg bg-white/10 px-3 pt-3 sm:rounded-xl sm:px-4 sm:pt-4'
            key={voice.name}
            role='listitem'
          >
            <span className='text-xs text-navy-220/70 sm:text-sm'>
              {voice.name}
            </span>
            <img
              alt={`Avatar for ${voice.name} voice`}
              className='mx-auto mt-4 h-auto w-full max-w-[180px] min-w-[120px] rounded-lg sm:mt-6 lg:mt-8'
              src={voice.image_url}
            />
            <div className='absolute -bottom-4 left-1/2 -translate-x-1/2'>
              {/* Unified control container with absolutely positioned button */}
              <div
                className={`audio-control-container ${
                  containerStates[voice.name] ? 'playing' : 'paused'
                }`}
              >
                {/* Absolutely positioned Play/Pause button */}
                <button
                  aria-label={
                    containerStates[voice.name]
                      ? `Pause ${voice.name} voice sample`
                      : `Play ${voice.name} voice sample`
                  }
                  aria-pressed={containerStates[voice.name]}
                  className='audio-button'
                  onClick={() => {
                    handlePlayPause(voice.name);
                  }}
                  type='button'
                >
                  {containerStates[voice.name] ? (
                    <IconPause className='text-navy-220' />
                  ) : (
                    <IconPlay className='text-navy-220' />
                  )}
                </button>

                {/* Progress bar - always rendered but conditionally visible */}
                <div
                  className={`progress-bar-container ${containerStates[voice.name] ? 'visible' : ''}`}
                >
                  <div className='progress-bar-track'>
                    <div
                      className='progress-bar-indicator'
                      style={{
                        width: `${String(progressValues[voice.name] ?? 0)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VoiceSelectionBlock;
